/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import Router from 'next/router';
import Script from 'next/script';
import cn from 'classnames';

import { useRouter } from 'libs/router/useRouter';
import { clearTimeoutIfExists } from 'libs/node';
import { content as contentApi } from 'libs/http/api/content/content';

import { ContentContext } from 'context/ContentContext';

import { useRequest } from 'hooks/useRequest';
import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { DownloadSample } from 'ui/molecules/DownloadSample/DownloadSample';
import { Breadcrumbs } from 'ui/atoms/Breadcrumbs/Breadcrumbs';
import { Faq } from 'ui/atoms/Faq/Faq';
import { DynamicImport } from 'ui/DynamicImport';

import { BlogNote } from 'features/articles/atoms/BlogNote/BlogNote';
import { Benefits } from 'features/articles/organisms/Benefits/Benefits';
import { Collapse } from 'features/articles/organisms/Collapse/Collapse';
import { ReadingBar } from 'features/articles/organisms/ReadingBar/ReadingBar';
import { BookDemoLoader } from 'features/articles/organisms/BookDemo/BookDemoLoader';
import { FindMoreLoader } from 'features/articles/organisms/FindMore/FindMoreLoader';
import { BlogSidebarLoader } from 'features/articles/organisms/BlogSidebar/BlogSidebarLoader';

import styles from './ArticlePage.module.scss';

import {
  addLazyLoading,
  downloadSamplePattern,
  findOutPattern,
  formPattern,
  getLastTitleKey,
  getTitles,
  parsePropsOfTag,
  tagsPattern,
} from './utils';

// Browser does not execute <script> tags inserted via innerHTML (used by dangerouslySetInnerHTML).
// On SSR/hard reload they run during HTML parse; on SPA navigation they must be re-created.
let hasSpaNavigated = false;
if (typeof window !== 'undefined') {
  Router.events.on('routeChangeStart', () => {
    hasSpaNavigated = true;
  });
}

const ArticlePage = () => {
  const { t } = useInt();

  const { query, asPath } = useRouter();
  const { data: content, secondaryNavigation, pageVars, links, additionals } = useContext(ContentContext);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollArticlesRef = useRef<HTMLDivElement>(null);
  const collapseDebounce = useRef<NodeJS.Timeout>(undefined);
  const firstScriptEffect = useRef(true);

  type CollapsedKey = (typeof titles)[number]['key'] | null;
  const [collapsed, setCollapsed] = useState<CollapsedKey>(getLastTitleKey(content.has_article));
  const [collapsedLoading, setCollapsedLoading] = useState<CollapsedKey>(null);

  const lastTag = useRef<string | null>(null);

  const commonPageVars = useMemo(() => pageVars.common || {}, [pageVars.common]);
  const article = useMemo(() => content.has_article, [content.has_article]);
  const titles = useMemo(() => getTitles(article), [article]);

  const {
    request: requestClientContent,
    data: dataClientContent,
    loading: loadingClientContent,
  } = useRequest<{ content: string }>({
    data: { content: '' },
  });

  const onClickContinueReading = useCallback(() => {
    requestClientContent(contentApi.staticArticleClient({ lang: `${query.lang}`, slug: `${query.route}` }));
  }, [query.lang, query.route, requestClientContent]);

  useEffect(() => {
    return () => {
      clearTimeoutIfExists(collapseDebounce.current);
    };
  }, []);

  const onCollapse = useCallback((key: (typeof titles)[number]['key'], value: boolean) => {
    clearTimeoutIfExists(collapseDebounce.current);

    lastTag.current = '';
    setCollapsed(null);

    if (value === false) {
      return;
    }

    setCollapsedLoading(key);

    window.requestAnimationFrame(() => {
      if (contentRef.current !== null) {
        const rect = contentRef.current.getBoundingClientRect();
        window.scrollTo(0, rect.top + window.scrollY - 125);
      }

      collapseDebounce.current = setTimeout(() => {
        lastTag.current = '';
        setCollapsed(key);
        setCollapsedLoading(null);
      }, 200);
    });
  }, []);

  const renderFormTag = useCallback(
    (tag: string, key = 0) => {
      const { button, text } = parsePropsOfTag(tag) || {};

      return (
        <DynamicImport fallback={() => <BookDemoLoader />}>
          {() => {
            const Component = dynamic(() => import('features/articles/organisms/BookDemo/BookDemo'), {
              ssr: false,
              loading: () => <BookDemoLoader />,
            });

            return (
              <Component
                key={key}
                type="book"
                text={text}
                button={button}
                className={styles[`${titles.length > 1 ? 'articles-collapse-book-demo' : 'book-demo'}`]}
              />
            );
          }}
        </DynamicImport>
      );
    },
    [titles],
  );

  const renderFindOutTag = useCallback(
    (tag: string, key = 0) => {
      const { button, title, text } = parsePropsOfTag(tag) || {};

      return (
        <DynamicImport fallback={() => <FindMoreLoader />}>
          {() => {
            const Component = dynamic(() => import('features/articles/organisms/FindMore/FindMore'), {
              ssr: false,
              loading: () => <FindMoreLoader />,
            });

            return (
              <Component
                key={key}
                title={title}
                text={text}
                button={button}
                className={styles[`${titles.length > 1 ? 'articles-collapse-find-more' : 'find-more'}`]}
              />
            );
          }}
        </DynamicImport>
      );
    },
    [titles],
  );

  const renderDownloadSampleTag = useCallback(
    (tag: string, key = 0) => {
      const { button, text } = parsePropsOfTag(tag) || {};

      return (
        <DownloadSample
          key={key}
          title={text}
          button={button || article?.sample_title || commonPageVars.request_sample_button}
        />
      );
    },
    [article?.sample_title, commonPageVars.request_sample_button],
  );

  const wrapTables = useCallback((content: string): string => {
    return content.replace(
      /<table([^>]*)>([\s\S]*?)<\/table>/g,
      (_: string, attributes: string, tableContent: string): string => {
        return `<div class="${styles['table-wrapper']}"><table${attributes}>${tableContent}</table></div>`;
      },
    );
  }, []);

  const addH2Anchors = useCallback((newContent: string): string => {
    let countParts = 0;

    return newContent
      .replace(/&quot;/g, '"')
      .replace(/<h2[^>]*>([\s\S]*?)<\/h2>/g, (_: string, innerText: string): string => {
        let cleanTitle: string = innerText.replace(/<[^>]+>/g, '');

        cleanTitle = cleanTitle.replace(/&#(\d+);/g, (_, dec: string) => String.fromCharCode(Number(dec)));

        const htmlEntitiesMap: Record<string, string> = {
          '&nbsp;': ' ',
          '&amp;': '&',
          '&ndash;': '–',
          '&hellip;': '…',
        };

        cleanTitle = cleanTitle
          .replace(/&nbsp;|&amp;|&ndash;|&hellip;/g, (entity) => htmlEntitiesMap[entity] || ' ')
          .trim();

        if (!cleanTitle) return '';

        countParts += 1;

        return `<h2 id="${countParts}" class="article-scroll">${cleanTitle}</h2>`;
      });
  }, []);

  const mergedArticleContent = useMemo(() => {
    if (!article?.content) {
      return '';
    }

    if (dataClientContent.content) {
      return article.content + dataClientContent.content;
    }

    return article.content;
  }, [article?.content, dataClientContent.content]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) {
      return;
    }

    // Inline handlers in static article markup (onclick="fn(...)") need top-level
    // function declarations to land on window. IIFE-wrap would localize them. Run
    // bare and dedupe by script text via a window-scoped set to avoid `const`
    // redeclaration on remount (A → B → A).
    const w = window as Window & { __articleScriptsRun?: Set<string> };
    if (!w.__articleScriptsRun) {
      w.__articleScriptsRun = new Set<string>();
    }

    const ran = w.__articleScriptsRun;

    const scripts = Array.from(container.querySelectorAll<HTMLScriptElement>('script'));

    if (firstScriptEffect.current && !hasSpaNavigated) {
      // Hard load: browser executed scripts during HTML parse. Record only.
      scripts.forEach((s) => {
        const src = s.getAttribute('src');
        if (src) ran.add(`src:${src}`);
        if (s.textContent) ran.add(s.textContent);
      });

      firstScriptEffect.current = false;

      return;
    }

    firstScriptEffect.current = false;

    // SPA nav: re-create scripts. Externals must load before inline that depends
    // on their globals (e.g. Chart.js + inline init), but dynamically inserted
    // scripts don't preserve order — inject externals first, await load, then
    // inline. DOMContentLoaded has already fired, so patch addEventListener for
    // the duration of inline injection to call DOMContentLoaded handlers inline.
    //
    // Mark `ran` BEFORE injection so React StrictMode's double-effect (and any
    // re-render on the same content) becomes a true no-op — the first pass owns
    // the in-flight load, the second pass sees everything queued and bails.
    // Cleanup intentionally does NOT remove injected scripts: external script
    // side-effects (window.Chart) must persist; inline init is idempotent over
    // the rendered canvases.
    const externalLoads: Promise<void>[] = [];
    const pendingInline: { text: string; attrs: Attr[] }[] = [];

    scripts.forEach((oldScript) => {
      const text = oldScript.textContent || '';
      const src = oldScript.getAttribute('src');

      if (src) {
        const key = `src:${src}`;
        if (ran.has(key)) {
          return;
        }

        ran.add(key);

        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
        newScript.async = false;
        externalLoads.push(
          new Promise<void>((resolve) => {
            newScript.addEventListener('load', () => resolve());
            newScript.addEventListener('error', () => resolve());
          }),
        );
        document.body.appendChild(newScript);

        return;
      }

      if (text && ran.has(text)) {
        return;
      }

      if (text) {
        ran.add(text);
      }

      pendingInline.push({ text, attrs: Array.from(oldScript.attributes) });
    });

    if (pendingInline.length === 0) {
      return;
    }

    Promise.all(externalLoads).then(() => {
      type DocWithListener = { addEventListener: typeof document.addEventListener };
      const docPatch = document as unknown as DocWithListener;
      const origAdd: typeof document.addEventListener = document.addEventListener.bind(document);
      docPatch.addEventListener = ((
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions,
      ) => {
        if (type === 'DOMContentLoaded' && typeof listener === 'function') {
          try {
            (listener as EventListener).call(document, new Event('DOMContentLoaded'));
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
          }

          return;
        }

        return origAdd(type, listener, options);
      }) as typeof document.addEventListener;

      try {
        pendingInline.forEach(({ text, attrs }) => {
          const newScript = document.createElement('script');
          attrs.forEach((attr) => newScript.setAttribute(attr.name, attr.value));
          newScript.textContent = text;
          document.body.appendChild(newScript);
        });
      } finally {
        docPatch.addEventListener = origAdd;
      }
    });
  }, [mergedArticleContent]);

  const processedContent = useMemo(() => {
    if (typeof mergedArticleContent !== 'string' || !mergedArticleContent) {
      return '';
    }

    return wrapTables(addLazyLoading(mergedArticleContent));
  }, [mergedArticleContent, wrapTables]);

  const renderContent = useCallback(
    (content?: string) => {
      if (typeof content !== 'string') {
        return null;
      }

      /** Save all h2's, attach anchors to each of them */
      /** Split content into small pieces so we can render call to action elements between them */
      /** Render either text or tag's pre-defined component */
      return wrapTables(addLazyLoading(addH2Anchors(content)))
        .split(tagsPattern)
        .map((item, key, arr) => {
          if (titles.length > 1 && key === arr.length - 2 && item.match(tagsPattern)) {
            lastTag.current = item;

            return null;
          }

          if (item.match(downloadSamplePattern)) {
            return renderDownloadSampleTag(item, key);
          }

          if (item.match(formPattern)) {
            return renderFormTag(item, key);
          }

          if (item.match(findOutPattern)) {
            return renderFindOutTag(item, key);
          }

          return (
            <div key={key} className={styles['articles-post-wrapper']}>
              <div className={styles['articles-post-text']} dangerouslySetInnerHTML={{ __html: item }} />
            </div>
          );
        });
    },
    [wrapTables, addH2Anchors, titles.length, renderDownloadSampleTag, renderFormTag, renderFindOutTag],
  );

  const url = useMemo(
    () =>
      `${process.env.HOST || ''}${links.reduce((acc, current) => {
        if (current.key === query.lang) {
          acc = current.link;
        }

        return acc;
      }, '')}`,
    [links, query],
  );

  const breadcrumbs = useMemo(() => {
    const isCaseStudy = Boolean(article?.case_studies_type);
    const slug = secondaryNavigation[isCaseStudy ? 'customers' : 'blog']?.slug;

    return [
      { slug: slug, query: { paage: 1 }, name: secondaryNavigation[isCaseStudy ? 'customers' : 'blog']?.title, key: 1 },
      {
        /** redirect to customers page if article has case study type */
        slug: slug,
        query: {
          paage: 1,
          type: isCaseStudy ? article?.case_studies_type : article?.type !== 'all_topics' ? article?.type : '',
        },
        name: isCaseStudy
          ? commonPageVars[`case_studies_${article?.case_studies_type}`]
          : commonPageVars[`blog_${article?.type}`],
        key: 2,
      },
      { name: article?.title, isLast: true, key: 3 },
    ];
  }, [article?.case_studies_type, article?.type, article?.title, secondaryNavigation, commonPageVars]);

  if (!article) {
    return <div className={styles['w-layout-base-1 m-auto']}>{t("Oops! There's no article on Global CMS.")}</div>;
  }

  return (
    <>
      {!content.is_static_article_preview && (
        <ReadingBar
          scrollArticlesRef={scrollArticlesRef}
          className={titles.length > 1 && collapsed === null ? 'w-0' : ''}
        />
      )}

      <Head>
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
      </Head>

      <Script
        id="blog-posting-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: content.title,
            image: `${process.env.HOST || ''}/${content.background?.url}`,
            datePublished: content.created_at,
            mainEntityOfPage: `${process.env.HOST || ''}${asPath}`,
            description: content.meta_description,
            author: [
              {
                '@type': 'Person',
                name: article.has_author?.full_name,
                url: `${process.env.HOST || ''}/${secondaryNavigation.author.slug}?id=${article.has_author?.id}`,
              },
            ],
            publisher: {
              '@type': 'Organization',
              name: 'Global Database',
              url: 'https://www.globaldatabase.com/',
            },
          }),
        }}
      />

      <div className={styles['articles-simple']}>
        <div className={styles['articles-simple__layout']}>
          <Breadcrumbs data={breadcrumbs} />
        </div>

        <div ref={scrollArticlesRef}>
          <BlogNote />

          <div className={styles['articles-simple__content']} ref={contentRef}>
            {titles.length > 1 ? (
              <>
                <div className={styles['articles-collapse-block']}>
                  {titles.map((item) => {
                    const opened = collapsed === item.key;

                    return (
                      <Collapse
                        key={item.key}
                        title={item.data}
                        opened={opened}
                        onChange={(value) => onCollapse(item.key, value)}
                        iconType="arrow-3"
                        iconClassName={`${styles['title-arrow']} ${opened ? styles['collapsed'] : ''}`}
                        className={styles['articles-post-wrapper']}
                        loading={collapsedLoading === item.key}
                      >
                        {opened ? renderContent(article[`content${item.key}`]) : null}
                      </Collapse>
                    );
                  })}
                </div>

                {lastTag.current && (
                  <>
                    {lastTag.current.match(downloadSamplePattern) && renderDownloadSampleTag(lastTag.current)}
                    {lastTag.current.match(formPattern) && renderFormTag(lastTag.current)}
                    {lastTag.current.match(findOutPattern) && renderFindOutTag(lastTag.current)}
                  </>
                )}
              </>
            ) : (
              <div className={styles['articles-simple__article']}>
                <div>
                  {renderContent(mergedArticleContent)}

                  {content.staticArticleHasClient && !dataClientContent.content && (
                    <div className={styles['continue-reading-wrapper']}>
                      <h5 className={styles['continue-reading-teaser']}>
                        <span className="block mb-5">{t("There's more to this article.")}</span>
                        {t('Continue reading to explore the full breakdown.')}
                      </h5>
                      <Button
                        type="blue"
                        loading={loadingClientContent}
                        loadingDelay={0}
                        onClick={onClickContinueReading}
                      >
                        {t('Continue reading')}
                      </Button>
                    </div>
                  )}
                </div>

                {article && (
                  <DynamicImport fallback={() => <BlogSidebarLoader />} delay={5000} threshold={0}>
                    {() => {
                      const Component = dynamic(() => import('features/articles/organisms/BlogSidebar/BlogSidebar'), {
                        ssr: false,
                        loading: () => <BlogSidebarLoader />,
                      });

                      return (
                        <Component
                          author={article.has_author}
                          route={secondaryNavigation.author.slug}
                          content={processedContent}
                        />
                      );
                    }}
                  </DynamicImport>
                )}
              </div>
            )}
          </div>
        </div>

        <Benefits
          hasSliderFeature
          hasEmptyPlaceholder={false}
          withViewMore
          title={commonPageVars.benefits_title}
          className={cn(styles['articles-benefits-wrapper'], additionals.faq && styles['mb-0'])}
          limit={9}
        />

        {additionals.faq && additionals.faq.data.length > 0 && (
          <Faq title={commonPageVars.faq_2} data={additionals.faq.data} />
        )}
      </div>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default ArticlePage;
